import axios, { type AxiosRequestConfig, type AxiosResponse } from "axios";
import type { ApiResponse } from "./ApiResponse";
import {
  showError,
  showSuccess,
  extractErrorMessage,
} from "../errorservices/errorservices";

import {
  adminMenuInviteUserEndPoints,
  alterNativeDashboardLayout,
  appmenuEndPoints,
  authEndPoints,
  customeronboardingBankEndPoints,
  customerSysMenuEndPoints,
  customerSysModuleEndPoints,
  customerSysPortalEndPoints,
  customerSysQuestEndPoints,
  customerSysSubFeatureEndPoints,
  customerUserEndPoints,
  customerUserRoleEndPoints,
  onboardingEndPoints,
  selfOnboardEndPoints,
  userSettingsServiceEndPoints,
} from "../urlConfig/apiEndpoints";

import type {
  CompanyUpdatepayload,
  CompanyView,
  SmeFileUploadPayload,
  SmeFileUploadResponse,
  SmeUsersCompanyProfileUpdatePayload,
  SmeUsersCompanyProfileUpdateResponse,
  UserCompanyPayload,
  UserPayload,
  UserUpdate,
  UserUpdatePayload,
  UserView,
} from "../../types/userprofile";

import type { Menulist } from "../../types/Menulist";
import type {
  CompanyBank,
  ContactValidatePayload,
  FileViewPayload,
  GetCompanyRoleAllResponse,
  GetSelfOnboardRejectApiResponse,
  GetSelfOnboardUpdateApiResponse,
  GetSelfOnboardUsingDefaultsApiResponse,
  GetSmeAccountsFeaturesListApiResponse,
  GetUsersByCompanyResponse,
  GxLookupTypeApiResponse,
  GxSmeFeatureResponse,
  GxSmeUserFeaturelistPayload,
  LookupTypePayload,
  RolePermisionsApiResponse,
  RolePermisionsPayload,
  SelfOnboardgxSmeFeaturesListApiPayload,
  SelfOnboardPayload,
  SelfOnboardRejectPayload,
  SelfOnboardUpdatePayload,
  SelfOnboardUsingDefaultsPayload,
  SmeAccountsFeaturesListPayload,
  SmeFeatureFee,
  SmeFeaturethirdprtyupdatePayload,
  SmeUserActivityStatusPayload,
  SmeUserDeletePayload,
  ThirdPartyFeature,
  ThirdPartySubFeatureListPayload,
} from "../../types/adminMenu";
import { getToken } from "@/app/services/context/tokenGetter";
import { executeLogout } from "@/app/utils/loginFlow/logoutHelper";
import { store, type RootState } from "@/app/redux/store";
import type {
  DashboardAccountsBalancePayload,
  DashboardAccountsCashPositionPayload,
  DashboardAccountsCashPositionResponse,
  DashboardBalanceAccountsPayload,
  DashboardBalanceAccountsResponse,
  DashboardLayoutViewPayload,
  DashboardLayoutViewResponse,
  DashboardPendingApprovalCountPayload,
  DashboardPendingApprovalCountResponse,
  DashboardPendingApprovalViewSummaryPayload,
  DashboardPendingApprovalViewSummaryResponse,
  DashboardPendingRepairCountPayload,
  DashboardPendingRepairCountResponse,
  DashboardPendingRepairViewSummaryPayload,
  DashboardPendingRepairViewSummaryResponse,
  DashboardUpcomingPaymentsPayload,
  DashboardUpcomingPaymentsResponse,
  DashboardLayoutListPayload,
  DashboardLayoutListResponse,
  DashboardTodayApprovedCountPayload,
  DashboardTodayApprovedCountResponse,
  DashboardSpendCategoryPayload,
  DashboardSpendCategoryResponse,
  DashboardAccountsMonthlyCashflowPayload,
  DashboardAccountsMonthlyCashflowResponse,
  DashboardAccount,
} from "@/app/types/alterNativeDashboardLayout";
import {
  CORRELATION_ID_HEADER,
  generateCorrelationId,
} from "@/app/utils/loginFlow/correlationId";

const getTenantId = (): string | undefined => {
  const state = store.getState() as RootState;
  return state.userData?.data?.tenantId;
};

// ---------------- Auth logout registration ----------------
const handleUnauthorized = (err: any) => {
  executeLogout();
  return Promise.reject(err);
};

const handleApiRequest = async <T>(
  method: typeof axios.get | typeof axios.post | typeof axios.delete,
  url: string,
  payload: any = {},
  showSuccessMsg?: boolean,
): Promise<ApiResponse<T>> => {
  try {
    const token = getToken();
    const tenantId = getTenantId();
    const correlationId = generateCorrelationId();
    const headers: Record<string, string> = {
      Accept: "*/*",
      [CORRELATION_ID_HEADER]: correlationId,
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (tenantId) {
      headers.appId = tenantId;
    }

    if (!(payload instanceof FormData))
      headers["Content-Type"] = "application/json";

    const config: AxiosRequestConfig = { headers };
    const response: AxiosResponse =
      method === axios.get || method === axios.delete
        ? await method(url, { ...config, params: payload })
        : await method(url, payload, config);

    const data: ApiResponse<T> = response.data;

    if (data?.status === 0) {
      if (showSuccessMsg && data.errorMessage) showSuccess(data.errorMessage);
      return data;
    }

    if (data?.status === 1) {
      if (showSuccessMsg && data.errorMessage) showSuccess(data.errorMessage);
      return data;
    }

    if (data?.status === 2) {
      showError(undefined, data.errorMessage || "Request failed.");
      throw new Error(data.errorMessage || "Request failed.");
    }

    throw new Error("Unexpected server response.");
  } catch (err: any) {
    const { code, message } = extractErrorMessage(err);

    if (code === 401) {
      return handleUnauthorized(err);
    }

    showError(code, message);
    return Promise.reject(err);
  }
};

// ---------------- Exported API methods, Customer, Company user role controller Get, Post, Put API List start ---------------
export const gxUserRolePutApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.put,
    customerUserRoleEndPoints.gxUserRolePut,
    payload,
  );
};

export const gxUserRolePostApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.post,
    customerUserRoleEndPoints.gxUserRolePost,
    payload,
  );
};

export const gxUserRoleSmeCompanyIdApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.post,
    customerUserRoleEndPoints.gxUserRoleSmeCompanyId,
    payload,
  );
};

export const gxUserRoleGetApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.get,
    customerUserRoleEndPoints.gxUserRoleGet,
    payload,
  );
};

export const gxUserRoleAllGetApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.get,
    customerUserRoleEndPoints.gxUserRoleAllGet,
    payload,
  );
};
// ---------------- Customer, Company user role controller Get, Post, Put API List end -----------------

// ---------------- Exported API methods, Customer, sys-sec-quest-controller Get, Post, Put API List start -----------------
export const gxSecQuestionsUpdateApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.put,
    customerSysQuestEndPoints.gxSecQuestionsUpdate,
    payload,
  );
};

export const gxSecQuestionsCreateApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.post,
    customerSysQuestEndPoints.gxSecQuestionsCreate,
    payload,
  );
};

export const gxSecQuestionsShowApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.get,
    customerSysQuestEndPoints.gxSecQuestionsShow,
    payload,
  );
};

export const gxSecQuestionsShowIdApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.get,
    customerSysQuestEndPoints.gxSecQuestionsShowId,
    payload,
  );
};

// ---------------- Customer, sys-sec-quest-controller Get, Post, Put API List start -----------------

// ---------------- Exported API methods, Customer, sys-portal-controller Get, Post, Put API List start ----------------
export const gxPortalUpdateApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.put,
    customerSysPortalEndPoints.gxPortalUpdate,
    payload,
  );
};

export const gxPortalCreateApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.post,
    customerSysPortalEndPoints.gxPortalCreate,
    payload,
  );
};

export const gxPortalShowApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.get,
    customerSysPortalEndPoints.gxPortalShow,
    payload,
  );
};

export const gxPortalShowIdApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.get,
    customerSysPortalEndPoints.gxPortalShowId,
    payload,
  );
};
// ---------------- Customer, sys-portal-controller Get, Post, Put API List end ----------------

// ---------------- Exported API methods, Customer, sys-module-controller Get, Post, Put API List start ----------------
export const gxModuleUpdateApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.put,
    customerSysModuleEndPoints.gxModuleUpdate,
    payload,
  );
};

export const gxModuleCreateApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.post,
    customerSysModuleEndPoints.gxModuleCreate,
    payload,
  );
};

export const gxModuleShowApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.get,
    customerSysModuleEndPoints.gxModuleShow,
    payload,
  );
};

export const gxModuleShowIdApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.get,
    customerSysModuleEndPoints.gxModuleShowId,
    payload,
  );
};

export const gxModulePortalIdApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.get,
    customerSysModuleEndPoints.gxModulePortalId,
    payload,
  );
};

// ---------------- Exported API methods, Customer, sys-menu-controller Get, Post, Put API List start ----------------
export const gxMenuUpdateApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.put,
    customerSysMenuEndPoints.gxMenuUpdate,
    payload,
  );
};

export const gxMenuCreateApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.post,
    customerSysMenuEndPoints.gxMenuCreate,
    payload,
  );
};

export const gxMenuIdApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.get,
    customerSysMenuEndPoints.gxMenuId,
    payload,
  );
};

export const gxMenuShowIdApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.get,
    customerSysMenuEndPoints.gxMenuShowId,
    payload,
  );
};

export const gxMenuModuleIdApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.get,
    customerSysMenuEndPoints.gxMenuModuleId,
    payload,
  );
};
// ---------------- Customer, sys-menu-controller Get, Post, Put API List end ----------------

// ---------------- Exported API methods, Customer, sys-sub-feature-controller Get, Post, Put API List start ----------------
export const gxSubFeatureEditIdApi = (payload: "Payload") => {
  return handleApiRequest<"Response">(
    axios.put,
    customerSysSubFeatureEndPoints.gxSysSubFeatureEditId,
    payload,
  );
};

// .........................company-user-controller....................................

export const gxUserprofileviewApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserEndPoints.gxsmeprofileview,
    payload,
  );
};

export const gxUserprofileuploadApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserEndPoints.gxsmeprofileupload,
    payload,
  );
};

export const gxUserListApi = (payload: UserPayload) => {
  return handleApiRequest<UserView>(
    axios.post,
    customerUserEndPoints.gxUserList,
    payload,
  );
};

export const gxUserviewApi = (payload: any) => {
  return handleApiRequest<UserView>(
    axios.post,
    customerUserEndPoints.gxUserview,
    payload,
  );
};

export const gxUserUpdateApi = (payload: SelfOnboardUpdatePayload) => {
  return handleApiRequest<UserUpdatePayload>(
    axios.post,
    customerUserEndPoints.gxUserupdate,
    payload,
  );
};

export const gxFeaturesListApi = (payload: UserUpdatePayload) => {
  return handleApiRequest<UserUpdate>(
    axios.post,
    customerUserEndPoints.gxFeaturesList,
    payload,
  );
};

export const gxCompanyviewApi = (payload: UserCompanyPayload) => {
  return handleApiRequest<CompanyView>(
    axios.post,
    customerUserEndPoints.gxCompanyview,
    payload,
  );
};

export const gxCompanyUpdateApi = (payload: CompanyUpdatepayload) => {
  return handleApiRequest<CompanyView>(
    axios.post,
    customerUserEndPoints.gxCompanyUpdate,
    payload,
  );
};

export const gxCompanytradeLicenseApi = (payload: CompanyUpdatepayload) => {
  return handleApiRequest<CompanyView>(
    axios.post,
    customerUserEndPoints.gxtradeLicense,
    payload,
  );
};

export const gxfileUploadApi = (payload: FormData) => {
  return handleApiRequest<CompanyView>(
    axios.post,
    customerUserEndPoints.gxfileUpload,
    payload,
  );
};

export const gxfileviewApi = (payload: FileViewPayload) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserEndPoints.gxfileview,
    payload,
  );
};
// ....

export const gxuserreportApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserEndPoints.gxsmeuserreport,
    payload,
  );
};

export const gxfetchreportApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserEndPoints.gxsmefetchreport,
    payload,
  );
};

export const gxuserfeedbackApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserEndPoints.gxsmeuserfeedback,
    payload,
  );
};

export const gxfetchfeedbackApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserEndPoints.gxsmefetchfeedback,
    payload,
  );
};

//---------------------Manage user api end-----------------

// .......................menu............................
export const gxMenuApi = (suppressToast = false) => {
  return handleApiRequest<Menulist>(
    axios.get,
    appmenuEndPoints.gxmenu,
    {},
    suppressToast,
  );
};

// .......................Bank list............................
export const gxBanklistApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserEndPoints.gxBanklist,
    payload,
  );
};

export const gxAuthBanklistApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserEndPoints.gxAuthBanklist,
    payload,
  );
};

// .......................Bank Account list............................
export const gxBankAccountlistApi = (suppressToast = false) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserEndPoints.gxBankAccountlist,
    suppressToast,
  );
};
// .......................Bank Account Delete............................
export const gxBankAccountDeleteApi = (suppressToast = false) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserEndPoints.gxBankAccountDelete,
    suppressToast,
  );
};
// .......................Bank Account activationState............................
export const gxBankAccountActivationStateApi = (suppressToast = false) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserEndPoints.gxBankAccountActivationState,
    suppressToast,
  );
};
// .......................gxcbspermissionlist...........................
export const gxcbspermissionlist = (suppressToast = false) => {
  return handleApiRequest<unknown>(
    axios.post,
    customerUserEndPoints.gxcbspermissionlist,
    {},
    suppressToast,
  );
};

// .......................gxaccount permissionlist............................
export const gxaccountpermissionlist = (suppressToast = false) => {
  return handleApiRequest<unknown>(
    axios.post,
    customerUserEndPoints.gxaccountpermissionlist,
    {},
    suppressToast,
  );
};

// .......................Customer user request............................
export const gxSelfOnboardApi = (payload: SelfOnboardPayload) => {
  return handleApiRequest<GetUsersByCompanyResponse>(
    axios.post,
    selfOnboardEndPoints.gxSelfOnboard,
    payload,
  );
};

export const gxCompanyRoleAllApi = (payload: { type: string }) => {
  return handleApiRequest<{ data: GetCompanyRoleAllResponse[] }>(
    axios.post,
    selfOnboardEndPoints.gxCompanyRoleAll,
    payload ?? {},
  );
};

export const gxlookupTypeApi = (payload: LookupTypePayload) => {
  return handleApiRequest<GxLookupTypeApiResponse>(
    axios.post,
    selfOnboardEndPoints.gxlookupType,
    payload,
  );
};

export const gxSelfOnboardUpdateApi = (payload: SelfOnboardUpdatePayload) => {
  return handleApiRequest<GetSelfOnboardUpdateApiResponse>(
    axios.post,
    selfOnboardEndPoints.gxSelfOnboardUpdate,
    payload,
  );
};

export const gxSmeFeaturesListApi = (
  payload: SelfOnboardgxSmeFeaturesListApiPayload,
) => {
  return handleApiRequest<{ data: GxSmeFeatureResponse[] }>(
    axios.post,
    selfOnboardEndPoints.gxSmeFeaturesList,
    payload,
  );
};

export const gxSmeFeaturesAccontsListApi = (suppressToast = false) => {
  return handleApiRequest<{ data: GxSmeFeatureResponse[] }>(
    axios.post,
    selfOnboardEndPoints.gxSmeFeaturesAccountList,
    {},
    suppressToast,
  );
};

export const gxSmeUserFeaturesAccontsListApi = (
  payload: GxSmeUserFeaturelistPayload,
) => {
  return handleApiRequest<{
    data: GxSmeFeatureResponse[];
  }>(axios.post, selfOnboardEndPoints.gxSmeFeaturesAccountList, payload, true);
};

export const gxSmeManageAccountsFeaturesListApi = (
  payload: SmeAccountsFeaturesListPayload,
) => {
  return handleApiRequest<GetSmeAccountsFeaturesListApiResponse>(
    axios.post,
    selfOnboardEndPoints.gxSmeManageAccountsFeaturesList,
    payload,
  );
};

export const gxSmeAccountsFeaturesListApi = (
  payload: SmeAccountsFeaturesListPayload,
) => {
  return handleApiRequest<GetSmeAccountsFeaturesListApiResponse>(
    axios.post,
    selfOnboardEndPoints.gxSmeAccountsFeaturesList,
    payload,
  );
};

export const gxGetCountryManageUserList = (payload: {}) => {
  return handleApiRequest<CompanyBank[]>(
    axios.post,
    selfOnboardEndPoints.gxGetCountriesManageUser,
    payload,
  );
};

export const gxSmeUserActivityStatusApi = (
  payload: SmeUserActivityStatusPayload,
) => {
  return handleApiRequest<{ data: GxSmeFeatureResponse[] }>(
    axios.post,
    customerUserEndPoints.gxUserActivityStatus,
    payload,
  );
};

export const gxSmeUserDeleteApi = (payload: SmeUserDeletePayload) => {
  return handleApiRequest<{ data: GxSmeFeatureResponse[] }>(
    axios.post,
    customerUserEndPoints.gxUserActivityStatusDelete,
    payload,
  );
};

export const gxSelfOnboardRejectApi = (payload: SelfOnboardRejectPayload) => {
  return handleApiRequest<GetSelfOnboardRejectApiResponse>(
    axios.post,
    selfOnboardEndPoints.gxSelfOnboardUpdate,
    payload,
  );
};

export const gxSelfOnboardUsingDefaultsApi = (
  payload: SelfOnboardUsingDefaultsPayload,
) => {
  return handleApiRequest<GetSelfOnboardUsingDefaultsApiResponse>(
    axios.post,
    selfOnboardEndPoints.gxSelfOnboardUsingDefaults,
    payload,
  );
};

export const gxKycFileUploadApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    selfOnboardEndPoints.gxKycFileUpload,
    payload,
  );
};

export const gxSelfOnboardViewApi = (payload: SelfOnboardPayload) => {
  return handleApiRequest<GetSelfOnboardRejectApiResponse>(
    axios.post,
    selfOnboardEndPoints.gxSelfOnboardView,
    payload,
  );
};

export const gxThirdPartyFeatureList = (payload: {}) => {
  return handleApiRequest<ThirdPartyFeature>(
    axios.post,
    customerUserEndPoints.gxThirdPartyFeatureList,
    payload,
  );
};

export const gxThirdPartySubFeatureList = (
  payload: ThirdPartySubFeatureListPayload,
) => {
  return handleApiRequest<SmeFeatureFee>(
    axios.post,
    customerUserEndPoints.gxThirdPartySubFeatureList,
    payload,
  );
};

export const gxThirdPartySubFeatureUpdate = (
  payload: SmeFeaturethirdprtyupdatePayload,
) => {
  return handleApiRequest<SmeFeatureFee>(
    axios.post,
    customerUserEndPoints.gxThirdPartyFeatureUpdate,
    payload,
  );
};

export const gxThirdPartyFeatureUpdate = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserEndPoints.gxThirdPartyMenuList,
    payload,
  );
};

export const gxThirdPartySubFeatureView = (payload: { featureId: string }) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserEndPoints.gxThirdPartySubFeatureView,
    payload,
  );
};

// .......................menu............................
export const growxsettings = (suppressToast = false) => {
  return handleApiRequest<Menulist>(
    axios.get,
    onboardingEndPoints.growxsettings,
    {},
    suppressToast,
  );
};

export const usersettings = (suppressToast = false) => {
  return handleApiRequest<any>(
    axios.post,
    userSettingsServiceEndPoints.gxUserSettingsUpdate,
    {},
    suppressToast,
  );
};

export const gxStgCreateApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    customeronboardingBankEndPoints.gxStgCreate,
    payload,
  );
};

// .......................menu............................
export const gxChangepasswordApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    onboardingEndPoints.growxchangepassword,
    payload,
  );
};

// .......................menu............................
export const gxkycdocumnetApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    adminMenuInviteUserEndPoints.gxkycdocumentupload,
    payload,
  );
};


// .......................gxgetsecurityquestionsApi............................
export const gxgetsecurityquestionsApi = (payload: { userId: any }) => {
  return handleApiRequest<any>(
    axios.post,
    onboardingEndPoints.growxgetsecurityquestions,
    payload,
  );
};

// .......................gxusersecurityquestionsApi............................
export const gxcreatesecurityquestionsApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    onboardingEndPoints.growxcreatesecurityquestions,
    payload,
  );
};

// .......................gxupdatesecurityquestionsApi............................
export const gxupdatesecurityquestionsApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    onboardingEndPoints.growxupdatesecurityquestions,
    payload,
  );
};

// .......................menu............................
export const gxContactValidateApi = (payload: ContactValidatePayload) => {
  return handleApiRequest<any>(
    axios.post,
    adminMenuInviteUserEndPoints.gxContactValidate,
    payload,
  );
};

export const gxRolePermisionsApi = (payload: RolePermisionsPayload) => {
  return handleApiRequest<RolePermisionsApiResponse>(
    axios.post,
    adminMenuInviteUserEndPoints.gxRolePermisions,
    payload,
  );
};

export const gxSmeGlobalCountryApi = (payload: UserCompanyPayload) => {
  return handleApiRequest<UserCompanyPayload>(
    axios.post,
    customerUserEndPoints.gxSmeGlobalCountry,
    payload,
  );
};

export const gxcompanycheckApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserEndPoints.gxcompanycheck,
    payload,
  );
};

export const gxsmeuserLookupsApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserEndPoints.gxsmeuserLookups,
    payload,
  );
};

export const gxSmeFileUploadApi = (payload: SmeFileUploadPayload) => {
  return handleApiRequest<SmeFileUploadResponse>(
    axios.post,
    customerUserEndPoints.gxSmeFileUpload,
    payload,
  );
};

export const gxSmeUsersCompanyProfileUpdateApi = (
  payload: SmeUsersCompanyProfileUpdatePayload,
) => {
  return handleApiRequest<SmeUsersCompanyProfileUpdateResponse>(
    axios.post,
    customerUserEndPoints.gxSmeUsersCompanyProfileUpdate,
    payload,
  );
};

// ===================================================
// Notification
// ===================================================

export const gxgrowxPreferenceListApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserRoleEndPoints.growxpreferencelist,
    payload,
  );
};

export const gxgrowxpreferencecreateApi = (payload: any) => {
  return handleApiRequest<any>(
    axios.post,
    customerUserRoleEndPoints.growxpreferencecreate,
    payload,
  );
};

// ===================================================
// SettingsInfo
// ===================================================
export const gxDashboardLayoutListApi = (
  payload: DashboardLayoutListPayload,
) => {
  return handleApiRequest<DashboardLayoutListResponse>(
    axios.post,
    alterNativeDashboardLayout.gxDashboardLayoutList,
    payload,
  );
};

export const gxDashboardLayoutViewApi = (
  payload: DashboardLayoutViewPayload,
) => {
  return handleApiRequest<DashboardLayoutViewResponse>(
    axios.post,
    alterNativeDashboardLayout.gxDashboardLayoutView,
    payload,
  );
};

export const gxDashboardUpcomingPaymentsApi = (
  payload: DashboardUpcomingPaymentsPayload,
) => {
  return handleApiRequest<DashboardUpcomingPaymentsResponse>(
    axios.post,
    alterNativeDashboardLayout.gxDashboardUpcomingPayments,
    payload,
  );
};

export const gxDashboardBalanceAccountsApi = (
  payload: DashboardBalanceAccountsPayload,
) => {
  return handleApiRequest<DashboardBalanceAccountsResponse>(
    axios.post,
    alterNativeDashboardLayout.gxDashboardBalanceAccounts,
    payload,
  );
};

export const gxDashboardPendingRepairViewSummaryApi = (
  payload: DashboardPendingRepairViewSummaryPayload,
) => {
  return handleApiRequest<DashboardPendingRepairViewSummaryResponse>(
    axios.post,
    alterNativeDashboardLayout.gxDashboardPendingRepairViewSummary,
    payload,
  );
};

export const gxDashboardPendingApprovalCountApi = (
  payload: DashboardPendingApprovalCountPayload,
) => {
  return handleApiRequest<DashboardPendingApprovalCountResponse>(
    axios.post,
    alterNativeDashboardLayout.gxDashboardPendingApprovalCount,
    payload,
  );
};

export const gxDashboardPendingApprovalViewSummaryApi = (
  payload: DashboardPendingApprovalViewSummaryPayload,
) => {
  return handleApiRequest<DashboardPendingApprovalViewSummaryResponse>(
    axios.post,
    alterNativeDashboardLayout.gxDashboardPendingApprovalViewSummary,
    payload,
  );
};

export const gxDashboardPendingRepairCountApi = (
  payload: DashboardPendingRepairCountPayload,
) => {
  return handleApiRequest<DashboardPendingRepairCountResponse>(
    axios.post,
    alterNativeDashboardLayout.gxDashboardPendingRepairCount,
    payload,
  );
};

export const gxDashboardAccountsCashPositionApi = (
  payload: DashboardAccountsCashPositionPayload,
) => {
  return handleApiRequest<DashboardAccountsCashPositionResponse>(
    axios.post,
    alterNativeDashboardLayout.gxDashboardAccountsCashPosition,
    payload,
  );
};

export const gxDashboardAccountsBalanceApi = (
  payload: DashboardAccountsBalancePayload,
) => {
  return handleApiRequest<DashboardAccount[]>(
    axios.post,
    alterNativeDashboardLayout.gxDashboardAccountsBalance,
    payload,
  );
};

export const gxDashboardTodayApprovedCountApi = (
  payload: DashboardTodayApprovedCountPayload,
) => {
  return handleApiRequest<DashboardTodayApprovedCountResponse>(
    axios.post,
    alterNativeDashboardLayout.gxDashboardTodayApprovedCount,
    payload,
  );
};

export const gxDashboardSpendCategoryApi = (
  payload: DashboardSpendCategoryPayload,
) => {
  return handleApiRequest<DashboardSpendCategoryResponse>(
    axios.post,
    alterNativeDashboardLayout.gxDashboardSpendCategory,
    payload,
  );
};

export const gxDashboardAccountsMonthlyCashflowApi = (
  payload: DashboardAccountsMonthlyCashflowPayload,
) => {
  return handleApiRequest<DashboardAccountsMonthlyCashflowResponse[]>(
    axios.post,
    alterNativeDashboardLayout.gxDashboardAccountsMonthlyCashflow,
    payload,
  );
};
